/**
 * 관리자 > 교사 관리 (/admin/bt-teachers)
 * 탭: 대시보드 · 교회별 교사 · 완성보고서 승인 · 수료증 발급·인쇄 · 보고서 보관함 · 교육 과정·강의 · 출석 코드 · 설정
 */
import React from 'react';
import { Link as RouterLink, useOutletContext, useSearchParams } from 'react-router-dom';
import { Badge, Box, Button, Tab, Tabs, ThemeProvider, Typography } from '@mui/material';
import { yellowTheme, YELLOW_GRADIENT, ON_YELLOW, C } from '../common';
import DashboardTab from './DashboardTab';
import ChurchTeachersTab from './ChurchTeachersTab';
import ReportsTab from './ReportsTab';
import CertificatesTab from './CertificatesTab';
import ArchiveTab from './ArchiveTab';
import CoursesTab from './CoursesTab';
import AttendanceTab from './AttendanceTab';
import SettingsTab from './SettingsTab';

const TABS = [
  { key: 'dashboard', label: '대시보드' },
  { key: 'churches', label: '교회별 교사' },
  { key: 'reports', label: '완성보고서 승인' },
  { key: 'certs', label: '수료증 발급·인쇄' },
  { key: 'archive', label: '보고서 보관함' },
  { key: 'courses', label: '교육 과정·강의' },
  { key: 'attend', label: '출석 코드' },
  { key: 'settings', label: '설정' },
];

type Ctx = { role?: string | null; certPending?: number; refreshCert?: () => void };

const BTTeacherHubPage: React.FC = () => {
  const ctx = (useOutletContext<Ctx>() || {}) as Ctx;
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab')! : 'dashboard';
  const go = (key: string, extra: Record<string, string> = {}) => setParams({ tab: key, ...extra });

  return (
    <ThemeProvider theme={yellowTheme}>
      <Box sx={{ mx: { xs: -2, sm: -3 }, mt: -3, bgcolor: C.bg, minHeight: 'calc(100vh - 64px)' }}>
        <Box sx={{ background: YELLOW_GRADIENT, color: ON_YELLOW, px: 3, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontSize={22} fontWeight={800}>교사 관리</Typography>
              <Typography fontSize={13} sx={{ opacity: 0.8 }}>교회 등록번호별 교사 · 완성보고서 승인 → BT 수료증 발급 · 교육 이력</Typography>
            </Box>
            <Button size="small" color="inherit" variant="outlined" onClick={() => window.open('/report', '_blank')}>교회 제출 화면 보기</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/admin/bt-teachers/manage">기존 교사 등록·편집</Button>
          </Box>
          <Tabs value={tab} onChange={(_, v) => go(v)} variant="scrollable" scrollButtons="auto"
            sx={{ mt: 1.5, '& .MuiTab-root': { color: ON_YELLOW, opacity: 0.75 }, '& .Mui-selected': { opacity: 1, color: `${ON_YELLOW} !important` }, '& .MuiTabs-indicator': { bgcolor: ON_YELLOW, height: 3 } }}>
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.key === 'reports' && ctx.certPending
                ? <Badge color="error" badgeContent={ctx.certPending} max={99} sx={{ pr: 1.5, '& .MuiBadge-badge': { right: -2, top: -2 } }}>{t.label}</Badge>
                : t.label} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {tab === 'dashboard' && <DashboardTab go={go} />}
          {tab === 'churches' && <ChurchTeachersTab initialIssue={params.get('issue') || undefined} />}
          {tab === 'reports' && <ReportsTab initialStatus={params.get('status') || undefined} batch={params.get('batch') || undefined} onCountsChanged={ctx.refreshCert} />}
          {tab === 'certs' && <CertificatesTab initialReceipt={params.get('receipt') || undefined} />}
          {tab === 'archive' && <ArchiveTab />}
          {tab === 'courses' && <CoursesTab goAttendance={() => go('attend')} />}
          {tab === 'attend' && <AttendanceTab />}
          {tab === 'settings' && <SettingsTab />}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default BTTeacherHubPage;
